import { ReactElement, ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppShell, useMantineTheme } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { IconSquareRoundedPlus } from "@tabler/icons-react";
import { ClientUserRole } from "src/graphql/generated/graphql";

import { IconCampaigns, IconSettings } from "../../assets/icons";
import { useAuthContext } from "../../hooks/auth";
import { withModals } from "../../hooks/modals";
import { LoginForm } from "../LoginForm";
import { HeaderComponent } from "./header";
import { LayoutNavbar, NavigationItem } from "./navbar";

export interface LayoutProps {
    children?: ReactNode;
}

export const Layout = withModals(({ children }: LayoutProps) => {
    const { t, i18n } = useTranslation(["common"]);
    const theme = useMantineTheme();
    const { isAuthenticated, client } = useAuthContext();

    const [isNavbarOpened, setIsNavbarOpened] = useState(false);

    const isAdmin = client?.role === ClientUserRole.Admin;
    const isOwner = client?.role === ClientUserRole.Owner;
    const isDeveloper = client?.role === ClientUserRole.Developer;

    const navigationItems = useMemo(() => {
        const navigationItems: NavigationItem[] = [{
            title: t("menu_campaigninquiries"),
            path: "/campaigns/inquiries",
            activeRegex: /(^\/campaigns\/inquiry$)|(^\/campaigns\/inquiry\/)|(^\/campaigns\/inquiries)/,
            icon: IconSquareRoundedPlus,
        }, {
            title: t("menu_campaigns"),
            path: "/campaigns",
            activeRegex: /(^\/campaign$)|(^\/campaign\/)|(^\/campaigns(?!(\/inquir)))/,
            icon: IconCampaigns,
        }];

        /*  if ([
            ClientUserRole.owner,
            ClientUserRole.admin,
            ClientUserRole.developer,
        ].indexOf(client?.role) >= 0) {
            navigationItems.push({
                title: t("menu_tenants"),
                path: "/tenants",
                activeRegex: /(^\/tenants$)|(^\/tenant\/)/,
                icon: IconSitemap,
            });
        }*/

        if (isOwner || isAdmin || isDeveloper) {
            navigationItems.push({
                title: t("menu_organization"),
                path: "/organization",
                activeRegex: /(^\/organization$)|(^\/organization\/)/,
                icon: IconSettings,
            });
        }

        return navigationItems;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language, client?.role]);

    const { width } = useViewportSize();
    const isMobile = width < 768;

    let header: ReactElement;
    if (isMobile) {
        header = (
            <HeaderComponent
                isNavbarOpened={isNavbarOpened}
                setIsNavbarOpened={setIsNavbarOpened}
            />
        );
    }

    let navbar: ReactElement;
    if (isAuthenticated) {
        navbar = (
            <LayoutNavbar
                isMobile={isMobile}
                isNavbarOpened={isNavbarOpened}
                items={navigationItems}
                onIsNavbarOpenedChange={setIsNavbarOpened}
            />
        );
    }

    return (
        <AppShell
            navbarOffsetBreakpoint="sm"
            asideOffsetBreakpoint="sm"
            padding={isMobile ? 20 : 40}
            styles={{
                main: {
                    background: theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[0],
                    overflowX: "hidden",
                },
            }}
            header={header}
            navbar={navbar}
        >
            {(isAuthenticated || /^\/password-reset/.test(location?.pathname)) ? children : <LoginForm />}
        </AppShell>
    );
});
